import {useCallback, useEffect, useEffectEvent, useRef, useState} from 'react';
import {
  type AgentSubType,
  type CollectionSubType,
  Concept,
  type EntityMainType,
} from 'types';
import {HTTP_STATUS_ABORTED, HttpError} from 'api/http/types';
import type {DataLoadStatus} from 'api/types';
import type {SearchResult} from 'services/elastic/types';
import {
  useLazySearchAgentQuery,
  useLazySearchSeriesQuery,
  useLazySearchWorkQuery,
} from 'services/elastic/elasticApi';
import {useLazySearchPrizeQuery} from 'services/prizes/prizesApi';
import {isConcreteLinkTargetEntity} from 'services/utils';
import {useColumnConfigState} from 'schema/table/hooks/useColumnConfigState';
import type {
  SearchMainEntityType,
  SearchResultValue,
  SearchSubEntityType,
} from '../types';
import {trimSearchQuery} from '../functions';

export type SearchLoadStatus<T> = DataLoadStatus<T>;

export const EMPTY_RESULT: SearchResult<SearchResultValue> = {
  hits: [],
  highlights: [],
  total: 0,
};
export const DEFAULT_RESULT: SearchLoadStatus<SearchResult<SearchResultValue>> =
  {
    status: 'Loaded',
    data: EMPTY_RESULT,
  };

export const useEntitySearch = (
  searchQuery: string,
  entityType: SearchMainEntityType,
  subtype: SearchSubEntityType | undefined,
  includeHachetteWorks?: boolean,
): SearchLoadStatus<SearchResult<SearchResultValue>> => {
  const [result, setResult] =
    useState<SearchLoadStatus<SearchResult<SearchResultValue>>>(DEFAULT_RESULT);
  const onSetResult = useEffectEvent(setResult);

  const searchQueryRef = useRef<string | undefined>(undefined);
  const pageRef = useRef<number | undefined>(undefined);
  const sizeRef = useRef<number | undefined>(undefined);
  const entityTypeRef = useRef<EntityMainType | undefined>(entityType);
  const subtypeRef = useRef<SearchSubEntityType | undefined>(subtype);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const includeHachetteWorksRef = useRef<boolean | undefined>(undefined);

  const schemaName = isConcreteLinkTargetEntity(entityType)
    ? entityType
    : (subtype ?? Concept.person);

  const {paginationModel, resetPaginationModel} = useColumnConfigState(
    schemaName === Concept.work ? 'workSummary' : schemaName,
  );

  const [triggerAgentSearch] = useLazySearchAgentQuery();
  const [triggerSeriesSearch] = useLazySearchSeriesQuery();
  const [triggerWorkSearch] = useLazySearchWorkQuery();
  const [triggerPrizeSearch] = useLazySearchPrizeQuery();

  const runSearch = useCallback(
    (
      type: SearchMainEntityType,
      query: string,
      subEntity: SearchSubEntityType | undefined,
      page: number,
      size: number,
      includeHachette: boolean | undefined,
    ): Promise<SearchResult<SearchResultValue>> => {
      switch (type) {
        case Concept.agent:
          return triggerAgentSearch({
            query,
            agentType: (subEntity ?? Concept.person) as AgentSubType,
            page,
            size,
          }).unwrap();
        case Concept.collection:
          return triggerSeriesSearch({
            query,
            seriesType: (subEntity ?? Concept.series) as CollectionSubType,
            page,
            size,
          }).unwrap();
        case Concept.work:
          return triggerWorkSearch({
            query,
            page,
            size,
            includeHachetteWorks: includeHachette,
          }).unwrap();
        case Concept.prize:
          return triggerPrizeSearch({query, page, size}).unwrap();
        default:
          throw new Error(`Unsupported entity type: ${type}`);
      }
    },
    [
      triggerAgentSearch,
      triggerSeriesSearch,
      triggerWorkSearch,
      triggerPrizeSearch,
    ],
  );

  // When search query or entity type/subtype is changed
  // => abort any ongoing search, search with new criteria
  useEffect(() => {
    if (
      searchQuery === searchQueryRef.current &&
      paginationModel.page === pageRef.current &&
      paginationModel.pageSize === sizeRef.current &&
      entityType === entityTypeRef.current &&
      subtype === subtypeRef.current &&
      includeHachetteWorks === includeHachetteWorksRef.current
    ) {
      return;
    }

    // Empty search is only allowed on prize.
    if (!searchQuery && entityType !== Concept.prize) {
      onSetResult(DEFAULT_RESULT);
      return;
    }

    const reset =
      (searchQuery ?? '') !== (searchQueryRef.current ?? '') ||
      entityType !== entityTypeRef.current ||
      subtype !== subtypeRef.current ||
      includeHachetteWorks !== includeHachetteWorksRef.current;

    if (reset && paginationModel.page > 0) {
      resetPaginationModel();
      pageRef.current = 0;
      sizeRef.current = 20;
    } else {
      pageRef.current = paginationModel.page;
      sizeRef.current = paginationModel.pageSize;
    }

    abortControllerRef.current = new AbortController();
    searchQueryRef.current = searchQuery;
    entityTypeRef.current = entityType;
    subtypeRef.current = subtype;
    includeHachetteWorksRef.current = includeHachetteWorks;

    onSetResult(prev => ({
      ...prev,
      status: 'Loading',
    }));
    const trimmedQuery = trimSearchQuery(searchQuery);
    runSearch(
      entityType,
      trimmedQuery,
      subtype,
      pageRef.current,
      sizeRef.current,
      includeHachetteWorks,
    )
      .then(data => {
        onSetResult({status: 'Loaded', data});
      })
      .catch(error => {
        if (
          error instanceof HttpError &&
          error.status === HTTP_STATUS_ABORTED
        ) {
          onSetResult(prev => ({
            ...prev,
            status: 'Loaded',
            data: prev.data ?? EMPTY_RESULT,
          }));
          // Caused by abort above, leave state as is - to be updated by next call
          return;
        }

        console.error('useEntitySearch: search failed', error);
        onSetResult({
          status: 'Failed',
          error,
        });
      });

    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = undefined;
    };
  }, [
    subtype,
    searchQuery,
    entityType,
    runSearch,
    resetPaginationModel,
    paginationModel.page,
    paginationModel.pageSize,
    includeHachetteWorks,
  ]);

  return result;
};
